/**
 * Who currently gets the features that are still being piloted.
 *
 * For the Inventory beta — the redesign and inventory results in global search,
 * which both read `UserStore.isInventoryPilot` — this list is OR-ed with the
 * PostHog `inventory-beta` experiment: whoever is named here
 * stays in regardless of how PostHog assigns them, which is also what covers
 * the window before flags resolve and PostHog being unreachable. Add NEW
 * testers in PostHog rather than here.
 *
 * Lives in constants, NOT in `helpers/featureFlags.js`, because `UserStore`
 * reads it too (for `isInventoryPilot`) and that helper imports the store —
 * putting the list there would be a circular import. Constants import nothing,
 * so both sides can share one list.
 *
 * Compared lower-cased on both sides, so entries can be written however the
 * person writes their own address.
 */
export const INVENTORY_PILOT_EMAILS = [
  // CollX
  'ryan@collx.app',
  'mitchell@collx.app',
  'ted@collx.app',
  'dshaw@collx.app',
  'dean@collx.app',
  // Team
  // 'kyle@hikyle.co',
  'cass@cardlens.com',
  'roman18888@gmail.com',
  'vinkmitchell5@gmail.com',
  'sofia@meremortals.ai',
  'station27cards@hotmail.com',
  // Dealers
  'dexterwinkler@comcast.net',
  'tjfitz2@yahoo.com',
  'grumpz@grumpzsportscards.com',
  'jessica.nehr@gmail.com',
  'kniqknaqzllc@gmail.com',
  'montereysportscards@gmail.com',
]

/**
 * The All Batches page inside Inventory (`/inventory/batches`) — the table that
 * replaces the legacy `/batches` card grid, in trial with two people before it
 * goes to the rest of the Inventory beta.
 *
 * Its OWN list: adding a name to `INVENTORY_PILOT_EMAILS` to hand someone this
 * page would hand them the whole redesign too. One list per feature.
 *
 * This list is AND-ed with the new design, never OR-ed: the page is built out of
 * new-design chrome and reads as broken inside the legacy shell, so being named
 * here is permission to see it, not a way around the redesign gate.
 */
export const ALL_BATCHES_PAGE_EMAILS = [
  'sofia@meremortals.ai',
  'kyle@hikyle.co',
]

/**
 * Batches created by an IMPORT (eBay / CollX / Shopify / CSV) — the backend
 * stamps `is_import` on them, and the app then names their source and takes
 * away the batch tools that only make sense for a scan (reprocess, banner,
 * adding cards, the type / sport / locked-set settings).
 *
 * Its OWN list, like the one above: adding a name to `INVENTORY_PILOT_EMAILS`
 * to show someone import batches would hand them the whole redesign too.
 *
 * Mitchell is listed under both addresses he appears under in
 * `INVENTORY_PILOT_EMAILS`, so this follows him whichever account he signs in
 * with. Drop the one he doesn't use if you know which it is.
 *
 * Everyone else sees import batches exactly as they do today: full tool set, no
 * source labels. Nothing is hidden from them that they can see now.
 */
export const IMPORT_BATCHES_EMAILS = [
  'kyle@hikyle.co',
  'mitchell@collx.app',
  'vinkmitchell5@gmail.com',
]

/** Case- and whitespace-insensitive membership, for the lists above. */
const onEmailList = (email, list) => {
  const normalized = String(email || '').trim().toLowerCase()

  return !!normalized && list.some(entry => entry.toLowerCase() === normalized)
}

/** True when `email` is on the pilot list. Case- and whitespace-insensitive. */
export const isPilotEmail = (email) => onEmailList(email, INVENTORY_PILOT_EMAILS)

/** True when `email` may see the All Batches page inside Inventory. */
export const isAllBatchesPageEmail = (email) => onEmailList(email, ALL_BATCHES_PAGE_EMAILS)

/** True when `email` may see import-batch labels and restrictions. */
export const isImportBatchesEmail = (email) => onEmailList(email, IMPORT_BATCHES_EMAILS)
